import { getLiveObjects } from '@/api-v2/web/strategies';
import type { LoadingWhenEnabledQRT, VQQueryOptions } from '@/types/VueQueryTypes';
import type { UndefinedInitialQueryOptions } from '@tanstack/vue-query';
import { queryOptions, useQuery } from '@tanstack/vue-query';
import { chainEnabled } from '@/utils/queries';
import useLoadingWhenEnabledQuery from './useLoadingWhenEnabledQuery';
import type { LiveObjectAccessRightDTO } from '@/types/LiveObjectAccessRightDTO';

export const liveObjectKeys = {
  all: () => [{ scope: 'live-objects' }] as const,
  list: () => [{ ...liveObjectKeys.all()[0], entity: 'list' }] as const,
};

/**
 * Get live objects (strategies and portfolios with shouldExposePositions = true)
 *
 * All items returned by this endpoint have shouldExposePositions = true
 * And then obviously `containsOnlyEquities` is returned as a property for each item in the list
 */
export function useLiveObjects(
  options: VQQueryOptions<LiveObjectAccessRightDTO[]> = {},
): LoadingWhenEnabledQRT<LiveObjectAccessRightDTO[], Error> {
  const enabled = chainEnabled(options.enabled);

  return useLoadingWhenEnabledQuery(
    useQuery<LiveObjectAccessRightDTO[], Error>({
      ...options,
      queryKey: liveObjectKeys.list(),
      queryFn: ({ signal }): Promise<LiveObjectAccessRightDTO[]> => getLiveObjects(signal),
    }),
    enabled,
  );
}

export const liveObjectsSelect = {
  byCode: (items: LiveObjectAccessRightDTO[]): Map<string, LiveObjectAccessRightDTO> => {
    return new Map(items.map((item) => [item.code, item]));
  },
  justCodes: (items: LiveObjectAccessRightDTO[]): Set<string> => {
    return new Set(items.map((item) => item.code));
  },
};

export function useLiveObjectsByCode(
  options: VQQueryOptions<LiveObjectAccessRightDTO[]> = {},
): LoadingWhenEnabledQRT<Map<string, LiveObjectAccessRightDTO>, Error> {
  const enabled = chainEnabled(options.enabled);

  return useLoadingWhenEnabledQuery(
    useQuery<LiveObjectAccessRightDTO[], Error, Map<string, LiveObjectAccessRightDTO>>({
      ...options,
      queryKey: liveObjectKeys.list(),
      queryFn: ({ signal }): Promise<LiveObjectAccessRightDTO[]> => getLiveObjects(signal),
      select: liveObjectsSelect.byCode,
    }),
    enabled,
  );
}

export function useLiveObjectCodes(
  options: VQQueryOptions<LiveObjectAccessRightDTO[]> = {},
): UndefinedInitialQueryOptions<LiveObjectAccessRightDTO[], Error, Set<string>> {
  return queryOptions<LiveObjectAccessRightDTO[], Error, Set<string>>({
    ...options,
    queryKey: liveObjectKeys.list(),
    queryFn: ({ signal }): Promise<LiveObjectAccessRightDTO[]> => getLiveObjects(signal),
    select: liveObjectsSelect.justCodes,
  });
}
